import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import TableCompo from "../CommonTable/TableCompo";
import { getSellHistoryRequest } from "../../redux/Sell/sellSlice";
import { getHoldingsRequest } from "../../redux/Dashboard/dashboardSlice";
import { useAppTheme } from "../../theme/useAppTheme";

const SellHistoryScreen = () => {
  const dispatch = useDispatch();

  const {
    data: sellHistory,
    loading,
    error,
  } = useSelector((state) => state.sell || {});
  const { data: holdings, appConfig } = useSelector(
    (state) => state.dashboard || {}
  );
  const decimal = appConfig?.decimalPrecision ?? 4;
  const [selectedMetal, setSelectedMetal] = useState("All");

  const themeColors = useAppTheme();
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);

  useEffect(() => {
    dispatch(getSellHistoryRequest());
    dispatch(getHoldingsRequest());
  }, [dispatch]);

  const metalOptions = useMemo(() => {
    if (!Array.isArray(holdings)) return [];
    const unique = [
      ...new Map(
        holdings
          .filter((item) => item?.bullion?.metalName)
          .map((item) => [
            `${item?.bullion?.metalName}-${item?.karat}-${item?.bullion?.purity}`,
            {
              label: `${item?.bullion?.metalName || ""} ${item?.karat || ""}${
                item?.bullion?.purity || ""
              }`,
              metalName: item?.bullion?.metalName,
              karat: item?.karat,
              purity: item?.bullion?.purity,
            },
          ])
      ).values(),
    ];
    return unique;
  }, [holdings]);

  const filteredSellHistory = useMemo(() => {
    if (!Array.isArray(sellHistory)) return [];
    if (selectedMetal === "All") return sellHistory;
    return sellHistory.filter((item) => {
      const label = `${item?.bullion?.metalName || ""} ${item?.bullion?.karat || ""}k-${
    item?.bullion?.purity || ""
  }`;
      return label.toLowerCase() === selectedMetal.toLowerCase();
    });
  }, [sellHistory, selectedMetal]);

  const columns = [
    { field: "metal", headerName: "Metal", flex: 1 },
    { field: "status", headerName: "Status", flex: 1 },
    { field: "date", headerName: "Date & Time", flex: 1.5 },
    { field: "bank", headerName: "Bank", flex: 1 },
    { field: "account", headerName: "Account No.", flex: 1 },
    { field: "weight", headerName: "Weight", flex: 0.5 },
  ];

  const tableData = filteredSellHistory.map((item, index) => ({
    id: index + 1,
    metal: `${item?.bullion?.metalName || "N/A"} ${
      item?.bullion?.karat || ""
    }k-${item?.bullion?.purity || ""}`,
    status: item?.status || "N/A",
    date: item?.createdAt
      ? new Date(item.createdAt).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "N/A",
    bank: item?.bankAccount?.bankName || "N/A",
    account: item?.bankAccount?.accNumber || "N/A",
    weight: item?.weight ? `${item.weight.toFixed(decimal)} gm` : "N/A",
  }));

  const paginatedData = tableData.slice(
  page * limit,
  page * limit + limit
  );


  const handleTableChange = (type, payload) => {
  if (type === "changePage") {
    setPage(payload.page);
  }

  if (type === "changeRowsPerPage") {
    setLimit(payload.rowsPerPage);
    setPage(0);
  }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        mt: 8,
      }}
    >
      <Card
        sx={{
          width: "98%",
          maxWidth: "1300px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          borderRadius: 3,
          background:themeColors.scaffoldGradientColors,
        }}
      >
        <CardContent>
          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: themeColors.primary,
              fontFamily: "Aeonik",
            }}
          >
            Sell History
          </Typography>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 1, mb: 2 }}
          >
            <FormControl size="small" sx={{ minWidth: 200, "& .MuiSelect-select": { color: themeColors.primary }, }}>
              <InputLabel>Filter Metal</InputLabel>
              <Select
                value={selectedMetal}
                onChange={(e) => setSelectedMetal(e.target.value)}
                label="Filter Metal"
                MenuProps={{
                    PaperProps: {
                      sx: { background: themeColors.mediumContainer, color: themeColors.text },
                    },
                  }}
              >
                <MenuItem value="All">All</MenuItem>
                {metalOptions.map((metal) => (
                  <MenuItem key={metal.label} value={metal.label}>
                    {metal.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box sx={{ textAlign: "center", mt: 10 }}>
              <CircularProgress color="primary" />
              <Typography variant="body2" mt={2}>
                Loading sell history...
              </Typography>
            </Box>
          ) : error ? (
            <Typography color="error" textAlign="center" mt={5}>
              {error}
            </Typography>
          ) : (
            <Box
              sx={{
                height: "70vh",
                width: "100%",
                minWidth: 0,
                overflow: "auto",
              }}
            >
              <TableCompo
                title="Sell History"
                columns={columns}
                data={paginatedData}
                page={page}
                limit={limit}
                totalCount={tableData.length}
                onTableChange={handleTableChange}
                loading={loading}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SellHistoryScreen;
